import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { CartProvider } from "./context/CartContext";
import { AuthProvider } from "./context/AuthContext";

// Customer Pages
import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import ProductPage from "./pages/ProductPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import FAQPage from "./pages/FAQPage";
import SizeGuidePage from "./pages/SizeGuidePage";

// Admin Pages
import AdminLoginPage from "./pages/admin/LoginPage";
import AdminDashboardPage from "./pages/admin/DashboardPage";
import AdminProductsPage from "./pages/admin/ProductsPage";
import AdminCategoriesPage from "./pages/admin/CategoriesPage";
import AdminOrdersPage from "./pages/admin/OrdersPage";
import AdminInventoryPage from "./pages/admin/InventoryPage";
import AdminCustomersPage from "./pages/admin/CustomersPage";
import AdminAnalyticsPage from "./pages/admin/AnalyticsPage";
import AdminTryOnPage from "./pages/admin/TryOnPage";

// Components
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { CartDrawer } from "./components/CartDrawer";
import { AdminLayout } from "./components/AdminLayout";
import { ProtectedRoute } from "./components/ProtectedRoute";

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <div className="min-h-screen bg-black">
            <Routes>
              {/* Redirect to admin on admin subdomain */}
              <Route path="/" element={
                window.location.hostname.startsWith('admin')
                  ? <Navigate to="/admin" replace />
                  : <CustomerLayout><HomePage /></CustomerLayout>
              } />
              <Route path="/tienda" element={<CustomerLayout><ShopPage /></CustomerLayout>} />
              <Route path="/tienda/:category" element={<CustomerLayout><ShopPage /></CustomerLayout>} />
              <Route path="/producto/:slug" element={<CustomerLayout><ProductPage /></CustomerLayout>} />
              <Route path="/nosotros" element={<CustomerLayout><AboutPage /></CustomerLayout>} />
              <Route path="/contacto" element={<CustomerLayout><ContactPage /></CustomerLayout>} />
              <Route path="/faq" element={<CustomerLayout><FAQPage /></CustomerLayout>} />
              <Route path="/guia-de-tallas" element={<CustomerLayout><SizeGuidePage /></CustomerLayout>} />
              
              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="/admin/analiticas" replace />} />
                <Route path="reportes" element={<AdminDashboardPage />} />
                <Route path="productos" element={<AdminProductsPage />} />
                <Route path="categorias" element={<AdminCategoriesPage />} />
                <Route path="pedidos" element={<AdminOrdersPage />} />
                <Route path="inventario" element={<AdminInventoryPage />} />
                <Route path="clientes" element={<AdminCustomersPage />} />
                <Route path="analiticas" element={<AdminAnalyticsPage />} />
                <Route path="probador" element={<AdminTryOnPage />} />
              </Route>
            </Routes>
            
            <CartDrawer />
            <Toaster 
              position="bottom-right" 
              theme="dark"
              toastOptions={{
                style: {
                  background: '#0A0A0A',
                  border: '1px solid rgba(201, 169, 98, 0.3)',
                  color: '#EDEDED',
                },
              }}
            />
          </div>
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

// Customer Layout Wrapper
const CustomerLayout = ({ children }) => (
  <>
    <Navbar />
    <main>{children}</main>
    <Footer />
    <WhatsAppButton />
  </>
);

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Floating WhatsApp Button - hidden on product pages (has its own bottom bar)
const WhatsAppButton = () => {
  const { pathname } = useLocation();
  const isProductPage = pathname.startsWith('/producto/');
  if (isProductPage) return null;
  return (
  <a
    href="https://wa.me/584242925792?text=Hola!%20Me%20gustaría%20obtener%20más%20información%20sobre%20D'Avocado%20Swimwear"
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#20BA5C] text-white p-4 rounded-full shadow-lg transition-transform duration-300 hover:scale-110 group"
    data-testid="whatsapp-button"
  >
    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
    <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-black/90 text-white text-sm px-3 py-2 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300">
      ¿Necesitas ayuda?
    </span>
  </a>
  );
};

export default App;
